import AskAiBar from './AskAiBar.jsx'
import HeroBriefCard from './HeroBriefCard.jsx'
import ReceiveInventoryScreen from './ReceiveInventoryScreen.jsx'
import ReorderSuggestionsScreen from './ReorderSuggestionsScreen.jsx'
import StocktakeScreen from './StocktakeScreen.jsx'
import VarianceScreen from './VarianceScreen.jsx'
import WasteTrackingScreen from './WasteTrackingScreen.jsx'
import { useHeroBrief } from '../hooks/useHeroBrief.js'

// Inventory hub — single tab in the top nav that holds the five
// stock-management screens as sub-views. They share a mental model
// ("things I do with my stock") so giving them their own top tab
// would have been five clicks of clutter on a phone.
//
// The sub-tab pill nav itself lives in App.jsx (inside the sticky
// amber-brand header) so it stays visible while scrolling inside a sub.
// This component renders the active sub-screen, prefixed with a
// charcoal AI hero brief that's scoped to the sub when the sub has
// rich data of its own (Receive, Reorder, Waste). Stocktake and
// Variance inherit the parent stock-hub brief — they don't have a
// big enough data surface to warrant their own Sonnet call.
//
// Sub-tab order matches frequency of use in the field:
//   Receive   — daily; almost always the first tap
//   Reorder   — daily/weekly check
//   Stocktake — weekly count
//   Variance  — weekly review against theoretical
//   Waste     — daily logging
export const STOCK_SUBS = [
  { id: 'receive',   label: 'Receive' },
  { id: 'reorder',   label: 'Reorder' },
  { id: 'stocktake', label: 'Stocktake' },
  { id: 'variance',  label: 'Variance' },
  { id: 'waste',     label: 'Waste' },
]

// Sub-keyed placeholder copy for the AskAiBar. Each sub gets a hint
// scoped to what the manager could plausibly ask about that surface,
// so the bar feels like context-aware AI rather than a generic
// "ask anything" search box.
const ASK_PLACEHOLDER_BY_SUB = {
  receive:   "Find this month's Sysco invoices…",
  reorder:   'Ask about your inventory…',
  stocktake: 'Ask about your stocktake…',
  variance:  'Ask about your variance counts…',
  waste:     'Why does Wednesday waste so much?',
}

// Sub-keys that get their own dedicated brief edge fn. Other subs
// fall back to the parent stock-hub brief (a synthesis the model
// already runs across the whole hub).
const HERO_SUB_BY_SUB = {
  receive: 'receive',
  reorder: 'reorder',
  waste:   'waste',
}

export default function InventoryHubScreen({ sub }) {
  // Hero brief scope: 'stock' parent for stocktake/variance, sub-specific
  // for the data-rich subs. The hook itself handles cache + edge call.
  const heroSubKey = HERO_SUB_BY_SUB[sub] || null
  const hero = useHeroBrief('stock', heroSubKey, { enabled: true })

  let body = null
  if (sub === 'receive')   body = <ReceiveInventoryScreen />
  if (sub === 'reorder')   body = <ReorderSuggestionsScreen />
  if (sub === 'stocktake') body = <StocktakeScreen />
  if (sub === 'variance')  body = <VarianceScreen />
  if (sub === 'waste')     body = <WasteTrackingScreen />
  if (!body) return null

  const placeholder = ASK_PLACEHOLDER_BY_SUB[sub] || 'Ask AI anything…'

  // The hero brief sits in its own wrapper at the top of the page,
  // BEFORE each sub-screen's <main> column. Each sub-screen owns
  // its own main wrapper with the right max-width and bottom
  // padding, which is why we don't try to nest hero + body in a
  // shared <main> here. The hero's `mx-4` margin matches what each
  // sub-screen uses internally, so the alignment is consistent on
  // mobile (and the hero gets its own centered max-width column).
  return (
    <>
      <div className="mx-auto max-w-screen-sm">
        <HeroBriefCard
          tabKey="stock"
          subKey={heroSubKey}
          phase={hero.phase}
          brief={hero.brief}
          error={hero.error}
          generatedAt={hero.generatedAt}
          onGenerate={hero.regenerate}
        />
      </div>
      {body}
      <AskAiBar placeholder={placeholder} />
    </>
  )
}
